/**
 * Products Dual-Write Test Suite
 *
 * This utility automatically creates products with various configurations
 * and verifies that Supabase and Firestore are in sync.
 *
 * Usage:
 *   import { runProductsDualWriteTests } from './utils/productsDualWriteTest';
 *   await runProductsDualWriteTests(db, testConfig);
 */

import { sanitizeForFirestore } from '../global/Firebase/productSanitize'
import {
  saveNewProduct,
  deleteProduct,
  updateStockManually,
} from '../global/Firebase/products';
import { api } from '../global/API/apiClient';
import { productsApi } from '../global/API/typedApiClient';
import { getDefaultStore } from 'jotai';
import {
  organizationAtom,
  storesDictAtom,
  productsDictAtom,
  dbAtom,
  userIdAtom,
  constantsAtom,
  productCategoriesDictAtom,
  suppliersDictAtom,
} from '../global/AtomsProvider';
import { validateProduct } from './productValidation';

// Mock translation function for tests - returns the key as a readable message
const mockT = (key) => {
  const messages = {
    'productsAndIngredients.nameEmptyError': 'Product name is required',
    'productsAndIngredients.categoryEmptyError': 'Category is required',
    'productsAndIngredients.supplierEmptyError': 'Supplier is required',
    'productsAndIngredients.productUnitEmptyError': 'Product unit is required',
    'productsAndIngredients.salePricesEmptyError': 'Sale prices cannot be empty',
    'productsAndIngredients.purchasePriceEmptyError': 'Purchase price is required',
    'productsAndIngredients.compositeNoIngredientsError': 'Composite products must have ingredients',
    'productsAndIngredients.imageStillUploadingError': 'Image is still uploading',
    'productsAndIngredients.barcodeAlreadyUsed': 'Barcode already used by: ',
    'notANumberError': 'Value must be a number',
  };
  return messages[key] || key;
};

// Test result tracking
const testResults = {
  passed: [],
  failed: [],
  skipped: [],
};

/**
 * All test variations to run
 */
export const PRODUCT_TEST_VARIATIONS = [
  // Basic CRUD (P2.1-P2.2)
  { id: 'P2.1', category: 'basic', name: 'Create with required fields only' },
  { id: 'P2.2', category: 'basic', name: 'Create with all optional fields' },

  // Relations (P2.3-P2.4)
  { id: 'P2.3', category: 'relations', name: 'Create with category reference' },
  { id: 'P2.4', category: 'relations', name: 'Create with supplier reference' },

  // Pricing (P2.5)
  { id: 'P2.5', category: 'pricing', name: 'Create with store-specific prices' },

  // Types (P2.6-P2.8)
  { id: 'P2.6', category: 'types', name: 'Create service type product' },
  { id: 'P2.7', category: 'types', name: 'Create composite product with ingredients' },
  { id: 'P2.8', category: 'types', name: 'Create with track_inventory=false' },

  // Inventory (P2.9)
  { id: 'P2.9', category: 'inventory', name: 'Create with initial stock values' },

  // Lifecycle (P2.10-P2.13)
  { id: 'P2.10', category: 'lifecycle', name: 'Update product fields', lifecycleAction: 'update' },
  { id: 'P2.11', category: 'lifecycle', name: 'Update prices', lifecycleAction: 'updatePrices' },
  { id: 'P2.12', category: 'lifecycle', name: 'Update stock manually', lifecycleAction: 'updateStock' },
  { id: 'P2.13', category: 'lifecycle', name: 'Delete product', lifecycleAction: 'delete' },
];

/**
 * Generate test product data for a specific variation
 */
export function generateTestProduct(variation, testData) {
  const {
    organizationId,
    storeId,
    categories,
    suppliers,
    existingProducts,
  } = testData;

  // Base product data
  const now = new Date();
  const timestamp = Date.now();

  const baseProduct = {
    organizationId,
    name: `Test Product ${variation.id} - ${timestamp}`,
    description: '',
    sku: '',
    barcode: '',
    categoryID: null, // Use categoryID for validateProduct
    supplierID: null, // Use supplierID for validateProduct
    purchasePrice: 0,
    packagePrice: 0,
    trackInventory: true,
    productUnit: 'шт', // Use productUnit for validateProduct
    type: false, // false = product, true = service
    isHiddenInNewSale: false,
    isComposite: false,
    manufacturedBeforeSale: false,
    ingredients: [],
    activeInStore: { [storeId]: true },
    salePrices: {}, // Use salePrices for validateProduct
    storeStock: {},
    lastChangeAt: now,
    lastChangeStore: storeId,
    dailyStatID: `test-${formatDateForDailyStat(now)}-${storeId}`,
  };

  // Apply variation
  switch (variation.category) {
    case 'basic':
      return applyBasicVariation(baseProduct, variation, testData);
    case 'relations':
      return applyRelationsVariation(baseProduct, variation, testData);
    case 'pricing':
      return applyPricingVariation(baseProduct, variation, testData);
    case 'types':
      return applyTypesVariation(baseProduct, variation, testData);
    case 'inventory':
      return applyInventoryVariation(baseProduct, variation, testData);
    case 'lifecycle':
      return applyLifecycleVariation(baseProduct, variation, testData);
    default:
      return baseProduct;
  }
}

function applyBasicVariation(product, variation, testData) {
  const { storeId, categories, suppliers } = testData;

  // Set required fields for all basic variations
  if (categories.length > 0) {
    product.categoryID = categories[0].id;
  }
  if (suppliers.length > 0) {
    product.supplierID = suppliers[0].id;
  }

  switch (variation.id) {
    case 'P2.1': // Required fields only
      product.name = `Test Basic Product ${Date.now()}`;
      product.salePrices = { [storeId]: 1000 }; // Required if not hidden
      break;

    case 'P2.2': // All optional fields
      product.name = `Test Full Product ${Date.now()}`;
      product.description = 'A comprehensive test product with all fields populated';
      product.sku = `SKU-${Date.now()}`;
      // Use numeric barcode since productValidation uses parseInt()
      product.barcode = String(200000 + Math.floor(Math.random() * 10000));
      product.purchasePrice = 500;
      product.packagePrice = 600;
      product.salePrices = { [storeId]: 1000 };
      product.storeStock = { [storeId]: 50 };
      break;
  }

  return product;
}

function applyRelationsVariation(product, variation, testData) {
  const { storeId, categories, suppliers } = testData;

  // Set base product with sale price
  product.salePrices = { [storeId]: 1000 };

  switch (variation.id) {
    case 'P2.3': // With category
      if (categories.length > 0) {
        product.categoryID = categories[0].id;
        product.name = `Test Product with Category ${Date.now()}`;
        // Set supplier too for validation
        if (suppliers.length > 0) {
          product.supplierID = suppliers[0].id;
        }
      } else {
        product._lifecycleData = {
          skip: true,
          skipReason: 'No categories available in test data',
        };
      }
      break;

    case 'P2.4': // With supplier
      if (suppliers.length > 0) {
        product.supplierID = suppliers[0].id;
        product.name = `Test Product with Supplier ${Date.now()}`;
        // Set category too for validation
        if (categories.length > 0) {
          product.categoryID = categories[0].id;
        }
      } else {
        product._lifecycleData = {
          skip: true,
          skipReason: 'No suppliers available in test data',
        };
      }
      break;
  }

  return product;
}

function applyPricingVariation(product, variation, testData) {
  const { stores, categories, suppliers } = testData;
  const storeIds = Object.keys(stores).slice(0, 3);

  // Set required fields for validation
  if (categories.length > 0) {
    product.categoryID = categories[0].id;
  }
  if (suppliers.length > 0) {
    product.supplierID = suppliers[0].id;
  }

  switch (variation.id) {
    case 'P2.5': // Store-specific prices
      product.name = `Test Multi-Price Product ${Date.now()}`;
      product.purchasePrice = 500;
      // Set different prices for different stores
      storeIds.forEach((storeId, index) => {
        product.salePrices[storeId] = 1000 + (index * 100);
        product.activeInStore[storeId] = true;
      });
      break;
  }

  return product;
}

function applyTypesVariation(product, variation, testData) {
  const { storeId, existingProducts, categories, suppliers } = testData;

  // Set required fields for validation
  if (categories.length > 0) {
    product.categoryID = categories[0].id;
  }
  if (suppliers.length > 0) {
    product.supplierID = suppliers[0].id;
  }

  switch (variation.id) {
    case 'P2.6': // Service type
      product.name = `Test Service ${Date.now()}`;
      product.type = true; // true = service
      product.trackInventory = false;
      product.salePrices = { [storeId]: 2000 };
      break;

    case 'P2.7': // Composite product
      product.name = `Test Composite Product ${Date.now()}`;
      product.isComposite = true;
      product.manufacturedBeforeSale = true;
      product.salePrices = { [storeId]: 1500 };
      // Add ingredients from existing products
      if (existingProducts.length >= 2) {
        product.ingredients = existingProducts.slice(0, 2).map((p, i) => ({
          id: p.id,
          name: p.name || 'Ingredient',
          quantity: i + 1,
          unit: p.productUnit || p.unit || 'шт',
          ref: p.ref,
          purchasePrice: p.purchasePrice || 100, // For calculating composite price
        }));
      } else {
        // Create test ingredients if no real products
        product.ingredients = [
          { id: 'test-ing-1', name: 'Test Ingredient 1', quantity: 2, unit: 'шт', purchasePrice: 100 },
          { id: 'test-ing-2', name: 'Test Ingredient 2', quantity: 1, unit: 'шт', purchasePrice: 150 },
        ];
      }
      break;

    case 'P2.8': // No inventory tracking
      product.name = `Test No Inventory Product ${Date.now()}`;
      product.trackInventory = false;
      product.salePrices = { [storeId]: 800 };
      break;
  }

  return product;
}

function applyInventoryVariation(product, variation, testData) {
  const { stores, categories, suppliers } = testData;
  const storeIds = Object.keys(stores).slice(0, 2);

  // Set required fields for validation
  if (categories.length > 0) {
    product.categoryID = categories[0].id;
  }
  if (suppliers.length > 0) {
    product.supplierID = suppliers[0].id;
  }

  switch (variation.id) {
    case 'P2.9': // Initial stock values
      product.name = `Test Stock Product ${Date.now()}`;
      product.purchasePrice = 500;
      product.trackInventory = true;
      // Set stock for multiple stores
      storeIds.forEach((storeId, index) => {
        product.storeStock[storeId] = 100 + (index * 50);
        product.salePrices[storeId] = 1000;
        product.activeInStore[storeId] = true;
      });
      break;
  }

  return product;
}

function applyLifecycleVariation(product, variation, testData) {
  const { storeId, categories, suppliers } = testData;

  // Set required fields for validation
  if (categories.length > 0) {
    product.categoryID = categories[0].id;
  }
  if (suppliers.length > 0) {
    product.supplierID = suppliers[0].id;
  }

  // Start with a basic product that can be modified
  product.name = `Test Lifecycle Product ${variation.id} - ${Date.now()}`;
  product.purchasePrice = 500;
  product.salePrices = { [storeId]: 1000 };
  product.storeStock = { [storeId]: 50 };

  // Store lifecycle action metadata
  product._lifecycleAction = variation.lifecycleAction;
  product._lifecycleData = {};

  switch (variation.id) {
    case 'P2.10': // Update fields
      product._lifecycleData = {
        updateFields: true,
        updateInfo: {
          name: `Updated Product Name ${Date.now()}`,
          description: 'Updated description after lifecycle action',
          purchasePrice: 600,
        },
      };
      break;

    case 'P2.11': // Update prices
      product._lifecycleData = {
        updatePrices: true,
        newPrices: { [storeId]: 1500 },
      };
      break;

    case 'P2.12': // Update stock manually
      product._lifecycleData = {
        updateStock: true,
        stockUpdate: {
          storeId,
          newStock: 100,
          previousStock: 50,
          comment: 'Test manual stock adjustment',
        },
      };
      break;

    case 'P2.13': // Delete
      product._lifecycleData = {
        deleteProduct: true,
      };
      break;
  }

  return product;
}

/**
 * Format date for dailyStatID (d-M-y format)
 */
function formatDateForDailyStat(date) {
  const d = date.getDate();
  const m = date.getMonth() + 1;
  const y = date.getFullYear();
  return `${d}-${m}-${y}`;
}

/**
 * Execute lifecycle action after product creation
 */
async function executeLifecycleAction(db, productRef, productData, testData) {
  const action = productData._lifecycleAction;
  const data = productData._lifecycleData;

  if (!action || !data) return { actionPerformed: null };

  // Handle skip case
  if (data.skip) {
    return { actionPerformed: 'skip', skipReason: data.skipReason };
  }

  // Wait a bit for initial sync
  await new Promise(resolve => setTimeout(resolve, 1000));

  try {
    if (data.updateFields) {
      // Update product fields via Firestore batch
      const { rootDb } = await import('../global/Firebase/firebase');
      const batch = rootDb.batch();
      batch.update(productRef, data.updateInfo);
      await batch.commit();

      // Dual-write to Supabase
      try {
        await productsApi.updateProduct(productRef.id, {
          name: data.updateInfo.name,
          description: data.updateInfo.description,
          purchasePrice: data.updateInfo.purchasePrice,
        });
      } catch (e) {
        console.warn('Supabase update failed:', e.message);
      }

      return { actionPerformed: 'updateFields' };
    }

    if (data.updatePrices) {
      // Update prices via Firestore
      const { rootDb, fieldValue } = await import('../global/Firebase/firebase');
      const batch = rootDb.batch();
      const updates = {};
      for (const [storeId, price] of Object.entries(data.newPrices)) {
        updates[`storePrices.${storeId}`] = price;
        updates[`salePrices.${storeId}`] = price;
      }
      batch.update(productRef, updates);
      await batch.commit();

      // Dual-write to Supabase
      try {
        await productsApi.updateProduct(productRef.id, {
          salePrices: data.newPrices,
        });
      } catch (e) {
        console.warn('Supabase price update failed:', e.message);
      }

      return { actionPerformed: 'updatePrices' };
    }

    if (data.updateStock) {
      const { storeId, newStock, previousStock, comment } = data.stockUpdate;
      await updateStockManually(
        db,
        productRef,
        storeId,
        newStock,
        previousStock,
        comment,
        productData.organizationId
      );
      // Dual-write to Supabase
      try {
        await productsApi.updateProduct(productRef.id, {
          storeStock: { [storeId]: newStock },
        });
      } catch (e) {
        console.warn('Supabase stock update failed:', e.message);
      }
      return { actionPerformed: 'updateStock' };
    }

    if (data.deleteProduct) {
      const jotaiStore = getDefaultStore();
      const userId = jotaiStore.get(userIdAtom) || 'test-user';
      await deleteProduct(db, productRef, productData, userId);
      return { actionPerformed: 'delete' };
    }
  } catch (error) {
    console.error('Lifecycle action failed:', error.message);
    return { actionPerformed: action, error: error.message };
  }

  return { actionPerformed: null };
}

/**
 * Run a single test variation
 */
async function runSingleTest(db, variation, testData, options = {}) {
  const { cleanup = true, delayMs = 5000 } = options;

  console.log(`\n--- Running test ${variation.id}: ${variation.name} ---`);

  try {
    // Generate test product
    const productData = generateTestProduct(variation, testData);

    // Check if test should be skipped
    if (productData._lifecycleData?.skip) {
      console.log(`SKIP: ${variation.name} - ${productData._lifecycleData.skipReason}`);
      testResults.skipped.push({ variation, reason: productData._lifecycleData.skipReason });
      return null;
    }

    // Validate product using production validation (same as UI would do)
    const validationOptions = {
      isNew: true,
      currentProductId: null,
      lastChangeSource: 'dualWriteTest',
      organizationId: testData.organizationId,
      userId: testData.uid,
      storeId: testData.storeId,
    };

    const { errors: validationErrors, formattedProduct } = validateProduct(
      productData,
      mockT,
      validationOptions
    );

    if (validationErrors.length > 0) {
      console.log(`VALIDATION FAILED: ${variation.name}`);
      console.log('Validation errors:', validationErrors);
      testResults.failed.push({
        variation,
        error: `Validation failed: ${validationErrors.join(', ')}`,
        validationErrors,
      });
      return null;
    }

    console.log(`Validation passed, creating product...`);

    // Merge formatted product with lifecycle metadata (needed for lifecycle tests)
    // Only include lifecycle fields if they're defined to avoid Firestore "undefined" errors
    const validatedProductData = {
      ...formattedProduct,
      ...(productData._lifecycleAction && { _lifecycleAction: productData._lifecycleAction }),
      ...(productData._lifecycleData && Object.keys(productData._lifecycleData).length > 0 && { _lifecycleData: productData._lifecycleData }),
    };

    // Create product via frontend code using validated/formatted product (this triggers dual-write)
    const productRef = await saveNewProduct(db, formattedProduct);
    const firestoreId = productRef.id;

    console.log(`Created product: ${firestoreId}`);

    // Wait for Supabase write to complete (CRITICAL: 5 second wait)
    await new Promise(resolve => setTimeout(resolve, delayMs));

    // For lifecycle tests, perform the action after initial creation
    let lifecycleResult = null;
    if (variation.category === 'lifecycle') {
      console.log(`Performing lifecycle action: ${variation.lifecycleAction}`);
      lifecycleResult = await executeLifecycleAction(db, productRef, validatedProductData, testData);
      console.log(`Lifecycle action result:`, lifecycleResult);

      // Handle skipped lifecycle tests
      if (lifecycleResult?.actionPerformed === 'skip') {
        console.log(`SKIP: ${variation.name} - ${lifecycleResult.skipReason}`);
        testResults.skipped.push({ variation, reason: lifecycleResult.skipReason });
        // Cleanup the created product
        if (cleanup) {
          try {
            const jotaiStore = getDefaultStore();
            const userId = jotaiStore.get(userIdAtom) || 'test-user';
            await deleteProduct(db, productRef, validatedProductData, userId);
          } catch (e) { /* ignore */ }
        }
        return null;
      }

      // Wait for the update to sync (CRITICAL: 5 second wait)
      await new Promise(resolve => setTimeout(resolve, delayMs));
    }

    // Compare using the API
    let comparisonResult;

    // Special case: if product was deleted, verify it's gone from both DBs
    if (lifecycleResult?.actionPerformed === 'delete') {
      comparisonResult = await verifyDeletion(firestoreId, productRef, db);
    } else {
      comparisonResult = await compareViaApi(firestoreId, productRef, db, validatedProductData);
    }

    // Log result
    if (comparisonResult.matches) {
      console.log(`PASS: ${variation.name}`);
      testResults.passed.push({
        variation,
        firestoreId,
        result: comparisonResult,
        lifecycleResult,
      });
    } else {
      console.log(`FAIL: ${variation.name}`);
      console.log('Differences:', JSON.stringify(sanitizeForFirestore(comparisonResult.differences), null, 2));
      testResults.failed.push({
        variation,
        firestoreId,
        result: comparisonResult,
        lifecycleResult,
      });
    }

    // Cleanup - delete the test product (skip if already deleted)
    if (cleanup && lifecycleResult?.actionPerformed !== 'delete') {
      try {
        const productDoc = await productRef.get();
        if (productDoc.exists) {
          const jotaiStore = getDefaultStore();
          const userId = jotaiStore.get(userIdAtom) || 'test-user';
          await deleteProduct(db, productRef, validatedProductData, userId);
          console.log(`Cleaned up product: ${firestoreId}`);
        }
      } catch (cleanupError) {
        console.warn(`Failed to cleanup product ${firestoreId}:`, cleanupError.message);
      }
    }

    return comparisonResult;
  } catch (error) {
    console.error(`ERROR in test ${variation.id}:`, error.message);
    console.error(`Stack trace:`, error.stack);
    testResults.failed.push({
      variation,
      error: error.message,
      stack: error.stack,
    });
    return null;
  }
}

/**
 * Compare product data via backend API
 */
async function compareViaApi(firestoreId, productRef, db, originalProductData) {
  try {
    const response = await api.get(`/products/compare/${firestoreId}`);
    return response.data;
  } catch (error) {
    console.warn('API comparison failed, falling back to manual:', error.message);
    return manualCompare(firestoreId, productRef, db, originalProductData);
  }
}

/**
 * Manual comparison between Firestore and Supabase
 */
async function manualCompare(firestoreId, productRef, db, originalProductData) {
  const differences = [];
  const warnings = [];

  // Get Firestore data
  let firestoreData = null;
  try {
    const doc = await productRef.get();
    if (doc.exists) {
      firestoreData = { id: doc.id, ...doc.data() };
    }
  } catch (error) {
    warnings.push(`Could not fetch from Firestore: ${error.message}`);
  }

  // Get Supabase data with retry logic
  let supabaseData = null;
  const maxRetries = 3;
  const retryDelayMs = 2000;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const response = await api.get('/products', {
        params: { firestoreId },
      });
      const products = response.data || [];
      supabaseData = products.find(p => p.firestore_id === firestoreId);

      if (supabaseData) {
        if (attempt > 1) {
          console.log(`Found product in Supabase on attempt ${attempt}`);
        }
        break;
      }

      if (attempt < maxRetries) {
        console.log(`Product not found in Supabase, retrying in ${retryDelayMs}ms...`);
        await new Promise(resolve => setTimeout(resolve, retryDelayMs));
      }
    } catch (error) {
      warnings.push(`Could not fetch from Supabase (attempt ${attempt}): ${error.message}`);
      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, retryDelayMs));
      }
    }
  }

  if (!supabaseData) {
    differences.push({
      field: 'product',
      supabaseValue: null,
      firestoreValue: 'exists',
      severity: 'critical',
    });
    return {
      firestoreId,
      foundInSupabase: false,
      foundInFirestore: !!firestoreData,
      matches: false,
      differences,
      warnings,
    };
  }

  // Field comparison
  const fieldsToCompare = [
    { supabase: 'name', firestore: 'name' },
    { supabase: 'description', firestore: 'description' },
    { supabase: 'sku', firestore: 'sku' },
    { supabase: 'barcode', firestore: 'barcode' },
    { supabase: 'base_purchase_price', firestore: 'purchasePrice' },
    { supabase: 'package_price', firestore: 'packagePrice' },
    { supabase: 'track_inventory', firestore: 'trackInventory' },
    { supabase: 'base_unit', firestore: 'unit' },
    { supabase: 'is_hidden_in_new_sale', firestore: 'isHiddenInNewSale' },
    { supabase: 'is_composite', firestore: 'isComposite' },
    { supabase: 'manufactured_before_sale', firestore: 'manufacturedBeforeSale' },
  ];

  for (const field of fieldsToCompare) {
    let supabaseValue = supabaseData[field.supabase];
    let firestoreValue = firestoreData?.[field.firestore];

    // Normalize values
    supabaseValue = normalizeValue(supabaseValue);
    firestoreValue = normalizeValue(firestoreValue);

    if (!valuesMatch(supabaseValue, firestoreValue)) {
      differences.push({
        field: field.supabase,
        supabaseValue,
        firestoreValue,
        severity: 'critical',
      });
    }
  }

  // Compare product_type
  let supabaseType = supabaseData.product_type;
  let firestoreType = firestoreData?.productType;

  // Handle Firestore legacy boolean type field
  if (firestoreType === undefined && firestoreData?.type !== undefined) {
    firestoreType = firestoreData.type === true ? 'service' : 'product';
  }

  if (!valuesMatch(normalizeValue(supabaseType), normalizeValue(firestoreType))) {
    differences.push({
      field: 'product_type',
      supabaseValue: supabaseType,
      firestoreValue: firestoreType,
      severity: 'critical',
    });
  }

  return {
    firestoreId,
    foundInSupabase: true,
    foundInFirestore: !!firestoreData,
    matches: differences.length === 0,
    differences,
    warnings,
  };
}

/**
 * Verify that a product has been deleted from both databases
 */
async function verifyDeletion(firestoreId, productRef, db) {
  const maxRetries = 15;
  const retryDelayMs = 2000;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    const differences = [];
    let firestoreExists = false;
    let supabaseExists = false;

    // Check Firestore
    try {
      const firestoreDoc = await productRef.get();
      firestoreExists = firestoreDoc.exists;
    } catch (error) {
      firestoreExists = false;
    }

    // Check Supabase
    try {
      const response = await api.get('/products', {
        params: { firestoreId },
      });
      const products = response.data || [];
      supabaseExists = products.some(p => p.firestore_id === firestoreId);
    } catch (error) {
      supabaseExists = false;
    }

    // Both deleted - success!
    if (!firestoreExists && !supabaseExists) {
      console.log(`Delete verified on attempt ${attempt}: both databases show deleted`);
      return {
        firestoreId,
        foundInSupabase: false,
        foundInFirestore: false,
        matches: true,
        differences: [],
        warnings: [],
      };
    }

    // Build differences for logging
    if (firestoreExists) {
      differences.push({
        field: 'firestore_deletion',
        supabaseValue: 'deleted',
        firestoreValue: 'still exists',
        severity: 'critical',
      });
    }
    if (supabaseExists) {
      differences.push({
        field: 'supabase_deletion',
        supabaseValue: 'still exists',
        firestoreValue: 'deleted',
        severity: 'critical',
      });
    }

    // If last attempt, return failure
    if (attempt === maxRetries) {
      console.log(`Delete verification failed after ${maxRetries} attempts`);
      return {
        firestoreId,
        foundInSupabase: supabaseExists,
        foundInFirestore: firestoreExists,
        matches: false,
        differences,
        warnings: [`Deletion not complete after ${maxRetries} retries`],
      };
    }

    // Wait and retry
    console.log(`Delete verification attempt ${attempt}/${maxRetries}: waiting...`);
    await new Promise(resolve => setTimeout(resolve, retryDelayMs));
  }

  return {
    firestoreId,
    foundInSupabase: true,
    foundInFirestore: true,
    matches: false,
    differences: [],
    warnings: ['Unexpected state in verifyDeletion'],
  };
}

function normalizeValue(value) {
  if (value === null || value === undefined) return null;
  if (value === '') return null;
  if (typeof value === 'number') return Number(value);
  if (typeof value === 'boolean') return value;
  return value;
}

function valuesMatch(a, b) {
  if ((a === null || a === undefined) && (b === null || b === undefined)) {
    return true;
  }
  // Treat null/undefined as equivalent to false for boolean fields
  if (typeof a === 'boolean' && (b === null || b === undefined)) {
    return a === false;
  }
  if (typeof b === 'boolean' && (a === null || a === undefined)) {
    return b === false;
  }
  if (a === null || a === undefined || b === null || b === undefined) {
    return false;
  }
  if (typeof a === 'number' && typeof b === 'number') {
    return Math.abs(a - b) < 0.01;
  }
  if (typeof a === 'boolean' || typeof b === 'boolean') {
    return Boolean(a) === Boolean(b);
  }
  return String(a) === String(b);
}

/**
 * Run all test variations
 */
export async function runProductsDualWriteTests(passedDb, options = {}) {
  const {
    variations = PRODUCT_TEST_VARIATIONS,
    cleanup = true,
    delayMs = 5000,
    stopOnFailure = false,
  } = options;

  // Reset results
  testResults.passed = [];
  testResults.failed = [];
  testResults.skipped = [];

  // Get test data from Jotai store
  const jotaiStore = getDefaultStore();
  const organization = jotaiStore.get(organizationAtom);
  const storesDict = jotaiStore.get(storesDictAtom);
  const productsDict = jotaiStore.get(productsDictAtom);
  const userId = jotaiStore.get(userIdAtom);

  // Get db from atom if not passed
  const db = passedDb || jotaiStore.get(dbAtom);

  if (!db) {
    throw new Error('Database not initialized. Please wait for app to fully load.');
  }

  if (!organization || Object.keys(organization).length === 0) {
    throw new Error('Organization not found. Please ensure you are logged in.');
  }

  if (!storesDict || Object.keys(storesDict).length === 0) {
    throw new Error('Stores not loaded. Please wait for app to fully load.');
  }

  // Get first store
  const storeId = Object.keys(storesDict)[0];
  if (!storeId) {
    throw new Error('No stores found. Please create a store first.');
  }

  const store = storesDict[storeId];

  // Get existing products for ingredient tests
  const existingProducts = Object.values(productsDict || {}).slice(0, 5);
  existingProducts.forEach(p => {
    if (!p.ref && p.id) {
      p.ref = db.collection('products').doc(p.id);
    }
  });

  // Get categories from Jotai atom (they're stored in productCategoriesDictAtom, not Firestore collection)
  const categoriesDict = jotaiStore.get(productCategoriesDictAtom) || {};
  const categories = Object.values(categoriesDict).slice(0, 5).map(cat => ({
    id: cat.id,
    name: cat.name,
    ...cat,
  }));
  console.log(`Loaded ${categories.length} categories for testing`);

  // Get suppliers from Jotai atom
  const suppliersDict = jotaiStore.get(suppliersDictAtom) || {};
  const suppliers = Object.values(suppliersDict).slice(0, 5).map(sup => ({
    id: sup.id,
    name: sup.name,
    ...sup,
  }));
  console.log(`Loaded ${suppliers.length} suppliers for testing`);

  const testData = {
    organizationId: organization.id,
    storeId,
    store,
    stores: storesDict,
    existingProducts,
    categories,
    suppliers,
    uid: userId || 'test-uid',
  };

  console.log('\n========================================');
  console.log('PRODUCTS DUAL-WRITE TEST SUITE');
  console.log('========================================');
  console.log(`Organization: ${organization.name || organization.id}`);
  console.log(`Store: ${store.name || storeId}`);
  console.log(`Existing products: ${existingProducts.length}`);
  console.log(`Categories: ${categories.length}`);
  console.log(`Suppliers: ${suppliers.length}`);
  console.log(`Variations to test: ${variations.length}`);
  console.log('========================================\n');

  // Run each test
  for (const variation of variations) {
    const result = await runSingleTest(db, variation, testData, { cleanup, delayMs });

    if (stopOnFailure && result && !result.matches) {
      console.log('\nStopping due to failure (stopOnFailure=true)');
      break;
    }
  }

  // Print summary
  console.log('\n========================================');
  console.log('TEST SUMMARY');
  console.log('========================================');
  console.log(`Passed: ${testResults.passed.length}`);
  console.log(`Failed: ${testResults.failed.length}`);
  console.log(`Skipped: ${testResults.skipped.length}`);
  console.log('========================================\n');

  if (testResults.failed.length > 0) {
    console.log('FAILED TESTS:');
    testResults.failed.forEach(({ variation, result, error }) => {
      console.log(`  - ${variation.id}: ${variation.name}`);
      if (error) {
        console.log(`    Error: ${error}`);
      } else if (result?.differences) {
        result.differences.forEach(diff => {
          console.log(`    ${diff.field}: Supabase=${JSON.stringify(sanitizeForFirestore(diff.supabaseValue))} vs Firestore=${JSON.stringify(sanitizeForFirestore(diff.firestoreValue))}`);
        });
      }
    });
  }

  return testResults;
}

/**
 * Run a quick smoke test (just a few critical variations)
 */
export async function runQuickProductsSmokeTest(db = null) {
  const quickVariations = [
    PRODUCT_TEST_VARIATIONS.find(v => v.id === 'P2.1'), // Required fields
    PRODUCT_TEST_VARIATIONS.find(v => v.id === 'P2.2'), // All fields
    PRODUCT_TEST_VARIATIONS.find(v => v.id === 'P2.6'), // Service type
    PRODUCT_TEST_VARIATIONS.find(v => v.id === 'P2.9'), // With stock
  ].filter(Boolean);

  return runProductsDualWriteTests(db, {
    variations: quickVariations,
    cleanup: true,
    delayMs: 5000,
  });
}

export default {
  runProductsDualWriteTests,
  runQuickProductsSmokeTest,
  generateTestProduct,
  PRODUCT_TEST_VARIATIONS,
};
